/**
 * SpaceWeatherDashboard.jsx
 * Full-featured real-time space weather dashboard matching SpaceWeatherLive.com
 * Data: NOAA SWPC, NASA SDO, NASA DONKI (all free, no key required)
 */
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import {
  Box, Grid, Typography, Card, Chip, Button, IconButton,
  Tooltip, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Divider, ToggleButton, ToggleButtonGroup,
  Skeleton, LinearProgress, CircularProgress,
} from '@mui/material';
import {
  Refresh, InfoOutlined, TrendingUp, TrendingDown,
  Remove, North, South, GpsFixed, Wifi, Flight,
  CheckCircle, WarningAmber, ErrorOutline,
} from '@mui/icons-material';
import api from '../services/api';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
  ReferenceLine, Cell,
} from 'recharts';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════════════════════════════════════
   CONSTANTS & THEME
   ═══════════════════════════════════════════════════════════════════════════ */
const C = {
  bg:         '#000000',
  card:       '#0d0d0d',
  cardBorder: 'rgba(255,255,255,0.12)',
  cyan:       '#ffffff',
  orange:     '#ffa726',
  red:        '#ef5350',
  green:      '#69f0ae',
  yellow:     '#ffca28',
  purple:     '#ce93d8',
  teal:       '#80cbc4',
  grey:       '#546e7a',
};

const glassCard = {
  background: 'linear-gradient(135deg, rgba(15,15,15,0.97) 0%, rgba(8,8,8,0.93) 100%)',
  backdropFilter: 'blur(12px)',
  border: `1px solid ${C.cardBorder}`,
  borderRadius: '16px',
  overflow: 'hidden',
};

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */
const getKpColor = (kp) => {
  if (kp < 3) return C.green;
  if (kp < 5) return C.yellow;
  if (kp < 7) return C.orange;
  return C.red;
};

const getKpStorm = (kp) => {
  if (kp < 4) return { level: 'G0', label: 'Quiet' };
  if (kp < 5) return { level: 'G1', label: 'Minor Storm' };
  if (kp < 6) return { level: 'G2', label: 'Moderate Storm' };
  if (kp < 7) return { level: 'G3', label: 'Strong Storm' };
  if (kp < 8) return { level: 'G4', label: 'Severe Storm' };
  return { level: 'G5', label: 'Extreme Storm' };
};

const getFlareInfo = (flux) => {
  if (!flux || flux < 1e-8) return { cls: 'A', color: C.green };
  if (flux < 1e-7)           return { cls: 'B', color: C.green };
  if (flux < 1e-6)           return { cls: 'C', color: C.yellow };
  if (flux < 1e-5)           return { cls: 'M', color: C.orange };
  return                            { cls: 'X', color: C.red };
};

const getFlareInfoFromStr = (s) => {
  switch (String(s)?.[0]?.toUpperCase()) {
    case 'X': return { cls: 'X', color: C.red };
    case 'M': return { cls: 'M', color: C.orange };
    case 'C': return { cls: 'C', color: C.yellow };
    default:  return { cls: s?.[0] || 'A', color: C.green };
  }
};

const fmtTime = (tag) => {
  try {
    return format(parseISO(String(tag).replace(' ', 'T').replace(/\.\d+Z?$/, 'Z')), 'HH:mm');
  } catch { return String(tag).slice(11, 16) || ''; }
};

const fmtDateTime = (tag) => {
  try {
    return format(parseISO(String(tag).replace(' ', 'T').replace(/\.\d+Z?$/, 'Z')), 'MMM d HH:mm');
  } catch { return String(tag).slice(0, 16) || ''; }
};

const fmtFlux = (v) => {
  if (!v || v <= 0) return '< A';
  const exp = Math.floor(Math.log10(v));
  const man = (v / Math.pow(10, exp)).toFixed(1);
  return `${man}×10^${exp} W/m²`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════════════════════════════════════ */
function useInterval(cb, delay) {
  const ref = useRef(cb);
  useEffect(() => { ref.current = cb; }, [cb]);
  useEffect(() => {
    if (delay == null) return;
    const id = setInterval(() => ref.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

/* ═══════════════════════════════════════════════════════════════════════════
   INFO TOOLTIP
   ═══════════════════════════════════════════════════════════════════════════ */
function InfoTip({ text }) {
  return (
    <Tooltip
      title={<Typography sx={{ fontSize: 12, lineHeight: 1.6 }}>{text}</Typography>}
      arrow placement="top"
    >
      <InfoOutlined sx={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', cursor: 'help', ml: 0.5, verticalAlign: 'middle' }} />
    </Tooltip>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION HEADER
   ═══════════════════════════════════════════════════════════════════════════ */
function SectionHeader({ title, subtitle, emoji, color = C.cyan }) {
  return (
    <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {emoji && <Typography sx={{ fontSize: 26 }}>{emoji}</Typography>}
      <Box>
        <Typography sx={{
          color, fontFamily: 'Orbitron, monospace', fontSize: { xs: 14, md: 16 },
          fontWeight: 800, letterSpacing: 1.5,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SVG CIRCULAR GAUGE
   ═══════════════════════════════════════════════════════════════════════════ */
function polarToCart(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function arcPath(cx, cy, r, from, to) {
  const s = polarToCart(cx, cy, r, from);
  const e = polarToCart(cx, cy, r, to);
  const large = to - from > 180 ? 1 : 0;
  return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
}

function CircularGauge({ value = 0, min = 0, max = 9, label, sublabel, color, size = 170 }) {
  const cx = size / 2, cy = size / 2 - 10;
  const r = size * 0.37;
  const frac = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const start = 135, sweep = 270;
  const fillEnd = start + frac * sweep;
  const svgH = size * 0.82;

  // Always show 1 decimal for the big number (e.g. "0.0" not "0").
  // Reason: Orbitron's '0' glyph has a diagonal slash (like Ø) which looks
  // broken when it's the only character. Two characters "0.0" avoids this.
  // Non-zero values like 2.33 → "2.3", 366.7 → "366.7" are unaffected.
  const displayVal = typeof value === 'number'
    ? value.toFixed(1)
    : (value || '--');

  return (
    /* Relative wrapper so the HTML overlay can be positioned over the SVG arcs */
    <Box sx={{ position: 'relative', display: 'inline-block', width: size, height: svgH }}>
      {/* SVG — arcs only, no text (SVG text can't reliably load Google Fonts) */}
      <svg width={size} height={svgH} viewBox={`0 0 ${size} ${svgH}`} style={{ display: 'block' }}>
        {/* Track */}
        <path d={arcPath(cx, cy, r, start, start + sweep)} fill="none"
          stroke="rgba(255,255,255,0.07)" strokeWidth={size * 0.055} strokeLinecap="round" />
        {/* Filled arc */}
        {frac > 0.005 && (
          <path d={arcPath(cx, cy, r, start, fillEnd)} fill="none"
            stroke={color} strokeWidth={size * 0.055} strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 7px ${color}90)` }} />
        )}
      </svg>

      {/* HTML overlay — fonts load reliably here */}
      <Box sx={{
        position: 'absolute',
        top: 0, left: 0, width: '100%',
        height: svgH,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
        mt: '-4px',
      }}>
        {/* Use Space Grotesk (already loaded) for the numeric value — clean zeros.
            Orbitron is kept for the label/sublabel where it looks great. */}
        <Typography sx={{
          color,
          fontFamily: '"Space Grotesk", "Roboto", sans-serif',
          fontSize: size * 0.20,
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: '-1px',
        }}>
          {displayVal}
        </Typography>
        <Typography sx={{
          color: 'rgba(255,255,255,0.45)',
          fontFamily: 'Orbitron, monospace',
          fontSize: size * 0.066,
          lineHeight: 1.3,
          mt: '2px',
        }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography sx={{
            color,
            fontFamily: 'Orbitron, monospace',
            fontSize: size * 0.063,
            fontWeight: 700,
            lineHeight: 1.2,
          }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SVG BZ VERTICAL BAR METER
   ═══════════════════════════════════════════════════════════════════════════ */
function BzMeter({ bz = 0, bt = 0 }) {
  const H = 120, W = 28, halfH = H / 2;
  const pxPerNt = halfH / 30;
  const barH = Math.min(Math.abs(bz) * pxPerNt, halfH);
  const barY = bz < 0 ? halfH : halfH - barH;
  const color = bz < 0 ? C.green : C.grey;
  const svgW = 90;
  const barX = (svgW - W) / 2;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* SVG — only geometry, no text to avoid font issues */}
      <svg width={svgW} height={H} viewBox={`0 0 ${svgW} ${H}`}>
        {/* Background track */}
        <rect x={barX} y={0} width={W} height={H} rx={5} fill="rgba(255,255,255,0.06)" />
        {/* Bar fill */}
        <rect x={barX} y={barY} width={W} height={Math.max(barH, 2)} rx={5} fill={color}
          style={{ filter: `drop-shadow(0 0 5px ${color}80)` }} />
        {/* Zero line */}
        <line x1={barX - 8} y1={halfH} x2={barX + W + 8} y2={halfH}
          stroke="rgba(255,255,255,0.28)" strokeWidth={1.5} strokeDasharray="4 2" />
      </svg>
      {/* Axis labels & value as HTML */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.1 }}>
          <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>+30</Typography>
          <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>0</Typography>
          <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>-30</Typography>
        </Box>
      </Box>
      {/* Current Bz value */}
      <Typography sx={{
        color, fontFamily: 'Orbitron, monospace',
        fontSize: 15, fontWeight: 700, mt: 0.5,
      }}>
        {bz > 0 ? '+' : ''}{bz?.toFixed(1)} nT
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
        Bt: {bt?.toFixed(1) ?? '--'} nT
      </Typography>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 1 — LIVE STATUS BAR
   ═══════════════════════════════════════════════════════════════════════════ */
function LiveStatusBar({ kpData, windData, bzData, xrayData, lastUpdated, isLoading, onRefresh }) {
  const kp = safeKp(kpData);
  const kpColor = getKpColor(kp);
  const storm = getKpStorm(kp);
  const speed = windData?.speed;
  const bz = bzData?.bz_gsm;
  const flare = getFlareInfo(xrayData?.flux || xrayData?.observed_flux);
  const bzColor = bz < 0 ? C.green : C.grey;
  const windColor = speed > 600 ? C.red : speed > 400 ? C.yellow : C.green;

  return (
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 110,
      background: 'rgba(0,0,0,0.96)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(255,255,255,0.14)',
      px: { xs: 1.5, md: 3 }, py: 0.75,
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 0.75, md: 1.25 },
    }}>
      {/* Kp */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: 10, fontFamily: 'Orbitron', mr: 0.3 }}>KP</Typography>
        <Chip label={kp.toFixed(1)} size="small"
          sx={{ bgcolor: `${kpColor}22`, color: kpColor, fontWeight: 800, fontFamily: 'Orbitron', fontSize: 12, height: 22, minWidth: 40 }} />
        <Chip label={storm.level} size="small"
          sx={{ bgcolor: `${kpColor}12`, color: kpColor, fontSize: 10, height: 18 }} />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 0.5 }} />

      {/* Solar Wind */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: 10, fontFamily: 'Orbitron' }}>WIND</Typography>
        <Typography sx={{ color: windColor, fontWeight: 700, fontSize: 13, fontFamily: 'Orbitron' }}>
          {speed ? `${Math.round(speed)} km/s` : '--'}
        </Typography>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 0.5 }} />

      {/* Bz */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: 10, fontFamily: 'Orbitron' }}>Bz</Typography>
        {bz != null && (bz < 0
          ? <South sx={{ fontSize: 13, color: C.green }} />
          : <North sx={{ fontSize: 13, color: C.grey }} />
        )}
        <Typography sx={{ color: bzColor, fontWeight: 700, fontSize: 13, fontFamily: 'Orbitron' }}>
          {bz != null ? `${bz > 0 ? '+' : ''}${bz.toFixed(1)} nT` : '--'}
        </Typography>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 0.5 }} />

      {/* X-Ray */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: 10, fontFamily: 'Orbitron' }}>XRAY</Typography>
        <Chip label={`${flare.cls}-class`} size="small"
          sx={{ bgcolor: `${flare.color}22`, color: flare.color, fontWeight: 700, fontSize: 11, height: 20 }} />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 0.5 }} />

      {/* Storm Level */}
      <Chip
        label={storm.level === 'G0' ? '● Quiet' : `⚡ ${storm.level} — ${storm.label}`}
        size="small"
        sx={{
          bgcolor: `${kpColor}18`,
          color: kpColor,
          fontWeight: 600,
          fontSize: 10,
          border: `1px solid ${kpColor}30`,
          animation: kp >= 5 ? 'pulse 2s ease-in-out infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.6 },
          },
        }}
      />

      <Box sx={{ flex: 1 }} />

      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, whiteSpace: 'nowrap' }}>
        {lastUpdated ? `Updated ${format(lastUpdated, 'HH:mm:ss')}` : 'Connecting…'}
      </Typography>
      <IconButton size="small" onClick={onRefresh} disabled={isLoading}
        sx={{ color: C.cyan, p: 0.4 }}>
        <Refresh sx={{
          fontSize: 15,
          animation: isLoading ? 'spin 1s linear infinite' : 'none',
          '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
        }} />
      </IconButton>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 2 — GAUGE CARDS
   ═══════════════════════════════════════════════════════════════════════════ */
// Safely parse a Kp value, clamped [0, 9].
// kp_index is only finalized every 3 hours — for the current incomplete window
// NOAA sends kp_index: null or 0. estimated_kp is the real-time 1-min estimate.
// Prefer estimated_kp (real-time) when kp_index is absent or 0.
const safeKp = (d) => {
  const finalized  = parseFloat(d?.kp_index);
  const estimated  = parseFloat(d?.estimated_kp);
  // Use finalized if it's a valid non-zero number, else fall back to estimated
  const raw = (!isNaN(finalized) && finalized > 0)
    ? finalized
    : (!isNaN(estimated) ? estimated : 0);
  return (isNaN(raw) || raw < 0 || raw > 9) ? 0 : raw;
};

function KpGaugeCard({ kpData, kpHistory }) {
  const current = safeKp(kpData);
  const color = getKpColor(current);
  const storm = getKpStorm(current);

  // Compare against reading from ~10 minutes ago for trend arrow
  const prevKp = safeKp(kpHistory?.slice(-10)?.[0]);
  const trend = current > prevKp + 0.3 ? 'up' : current < prevKp - 0.3 ? 'down' : 'flat';

  const miniData = useMemo(() =>
    (kpHistory || [])
      .filter((_, i, a) => i % Math.max(1, Math.floor(a.length / 20)) === 0)
      .slice(-20)
      .map(d => ({ t: fmtTime(d.time_tag), kp: safeKp(d) })),
    [kpHistory]
  );

  return (
    <Card sx={{ ...glassCard, height: '100%' }}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
            KP INDEX
            <InfoTip text="The Kp index measures global geomagnetic activity on a 0–9 scale. Values ≥5 indicate geomagnetic storms." />
          </Typography>
          <Chip label={storm.level} size="small"
            sx={{ bgcolor: `${color}22`, color, fontFamily: 'Orbitron', fontSize: 11, fontWeight: 700 }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularGauge value={current} min={0} max={9} color={color}
            label={storm.label} sublabel={storm.level} size={160} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mb: 1 }}>
          {trend === 'up'   && <TrendingUp sx={{ color: C.orange, fontSize: 16 }} />}
          {trend === 'down' && <TrendingDown sx={{ color: C.green, fontSize: 16 }} />}
          {trend === 'flat' && <Remove sx={{ color: 'text.secondary', fontSize: 16 }} />}
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
            {trend === 'up' ? 'Rising' : trend === 'down' ? 'Falling' : 'Stable'}
          </Typography>
        </Box>

        {miniData.length > 1 && (
          <ResponsiveContainer width="100%" height={44}>
            <BarChart data={miniData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Bar dataKey="kp" radius={2} maxBarSize={14}>
                {miniData.map((d, i) => <Cell key={i} fill={getKpColor(d.kp)} />)}
              </Bar>
              <YAxis domain={[0, 9]} hide />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
}

function SolarWindGaugeCard({ windData, windHistory }) {
  const hasData  = windData?.speed != null && windData.speed > 0;
  const speed    = hasData ? windData.speed : null;
  const density  = windData?.density  ?? null;
  const temp     = windData?.temperature ?? null;
  const color    = !speed ? C.grey : speed > 600 ? C.red : speed > 400 ? C.yellow : C.green;
  const label    = !speed ? 'NO DATA' : speed > 600 ? 'HIGH' : speed > 400 ? 'ELEVATED' : 'NORMAL';

  const miniData = useMemo(() => {
    const src = (windHistory || [])
      .filter((_, i, a) => i % Math.max(1, Math.floor(a.length / 30)) === 0)
      .slice(-30);
    return src.map(d => ({ t: fmtTime(d.time_tag), v: d.speed ?? null }));
  }, [windHistory]);

  const miniHasData = miniData.some(d => d.v != null);

  return (
    <Card sx={{ ...glassCard, height: '100%' }}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
            SOLAR WIND
            <InfoTip text="Solar wind speed from the ACE/DSCOVR spacecraft at L1 (1.5M km from Earth). Speeds above 600 km/s significantly raise storm risk." />
          </Typography>
          <Chip label={label} size="small"
            sx={{ bgcolor: `${color}22`, color, fontSize: 10 }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularGauge
            value={speed ? Math.min(speed, 900) : 0}
            min={200} max={900}
            color={color}
            label="km/s"
            sublabel={speed ? `${Math.round(speed)}` : '—'}
            size={160}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>Density</Typography>
            <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 700 }}>
              {density != null ? `${density.toFixed(1)} /cm³` : '—'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>Temp</Typography>
            <Typography sx={{ color: C.purple, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 700 }}>
              {temp ? `${(temp / 1000).toFixed(0)} kK` : '—'}
            </Typography>
          </Box>
        </Box>

        {miniHasData ? (
          <ResponsiveContainer width="100%" height={44}>
            <AreaChart data={miniData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="swMiniGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.cyan} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={C.cyan} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={C.cyan} fill="url(#swMiniGrad)"
                strokeWidth={1.5} dot={false} connectNulls />
              <YAxis hide domain={['auto', 'auto']} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 10 }}>
              Feed temporarily unavailable
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}

function BzGaugeCard({ bzData }) {
  const bz = bzData?.bz_gsm ?? 0;
  const bt = bzData?.bt ?? 0;
  const color = bz < 0 ? C.green : C.grey;
  const status = bz < -10 ? 'STORM RISK' : bz < 0 ? 'FAVORABLE' : 'QUIET';
  const statusColor = bz < -10 ? C.red : bz < 0 ? C.green : 'text.secondary';

  return (
    <Card sx={{ ...glassCard, height: '100%' }}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
            IMF Bz
            <InfoTip text="The interplanetary magnetic field Bz component. Sustained negative Bz (southward) opens Earth's magnetosphere to solar wind, triggering geomagnetic storms and auroras." />
          </Typography>
          <Chip label={status} size="small"
            sx={{ bgcolor: bz < 0 ? `${color}22` : 'rgba(255,255,255,0.06)', color: statusColor, fontSize: 10 }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <BzMeter bz={bz} bt={bt} />
        </Box>

        <Box sx={{
          mt: 1, p: 1.2, borderRadius: 1.5,
          bgcolor: bz < 0 ? 'rgba(105,240,174,0.06)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${bz < -10 ? C.red + '44' : bz < 0 ? C.green + '30' : 'rgba(255,255,255,0.05)'}`,
        }}>
          <Typography variant="caption" sx={{ color: bz < 0 ? C.green : 'text.secondary', textAlign: 'center', display: 'block', lineHeight: 1.5 }}>
            {bz < -15 ? '⚡ Strong southward Bz — active storm conditions'
              : bz < 0  ? '🌌 Southward Bz — aurora possible at high latitudes'
              :            '😴 Northward Bz — geomagnetically quiet'}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

function XRayGaugeCard({ xrayData, xrayHistory }) {
  const flux = xrayData?.flux || xrayData?.observed_flux || 0;
  const flare = getFlareInfo(flux);

  const peakFlux = useMemo(() =>
    xrayHistory?.length
      ? Math.max(...xrayHistory.map(d => d.flux || d.observed_flux || 0))
      : 0,
    [xrayHistory]
  );
  const peakFlare = getFlareInfo(peakFlux);

  // Log-scale position 0–100
  const logMin = -9, logMax = -3;
  const logFlux = flux > 0 ? Math.log10(flux) : logMin;
  const pct = Math.min(100, Math.max(0, ((logFlux - logMin) / (logMax - logMin)) * 100));

  return (
    <Card sx={{ ...glassCard, height: '100%' }}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
            X-RAY FLUX
            <InfoTip text="Solar X-ray flux measured by GOES satellites. Flare classes: A (weak) → B → C → M → X (strongest). M and X flares cause HF radio blackouts." />
          </Typography>
          <Chip label={`${flare.cls}-class`} size="small"
            sx={{ bgcolor: `${flare.color}22`, color: flare.color, fontFamily: 'Orbitron', fontWeight: 800, fontSize: 12 }} />
        </Box>

        {/* Class scale bar */}
        <Box sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            {[['A', C.green], ['B', C.green], ['C', C.yellow], ['M', C.orange], ['X', C.red]].map(([cls, col]) => (
              <Typography key={cls} sx={{
                flex: 1, textAlign: 'center',
                color: cls === flare.cls ? col : 'rgba(255,255,255,0.2)',
                fontFamily: 'Orbitron', fontSize: 14,
                fontWeight: cls === flare.cls ? 900 : 400,
              }}>{cls}</Typography>
            ))}
          </Box>
          <Box sx={{ position: 'relative', height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <Box sx={{
              position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`,
              background: `linear-gradient(90deg, ${C.green}, ${C.yellow}, ${C.orange}, ${C.red})`,
              borderRadius: 5, transition: 'width 0.6s ease',
              boxShadow: `0 0 10px ${flare.color}80`,
            }} />
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
          <Typography sx={{ color: flare.color, fontFamily: 'Orbitron', fontSize: 28, fontWeight: 900, lineHeight: 1 }}>
            {flare.cls}-class
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {fmtFlux(flux)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>Peak Today</Typography>
            <Chip label={`${peakFlare.cls}-class`} size="small"
              sx={{ bgcolor: `${peakFlare.color}22`, color: peakFlare.color, fontSize: 11, fontWeight: 700 }} />
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 3 — RECHARTS GRAPHS
   ═══════════════════════════════════════════════════════════════════════════ */
const chartTooltipStyle = {
  contentStyle: { background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12 },
  labelStyle: { color: 'rgba(255,255,255,0.5)' },
};

function KpBarChartCard({ data }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const step = Math.max(1, Math.floor(data.length / 48));
    return data
      .filter((_, i) => i % step === 0)
      .slice(-48)
      .map(d => {
        // kp_index is only finalized every 3 hours; use estimated_kp for recent slots
        const raw = parseFloat(d.kp_index) || parseFloat(d.estimated_kp) || 0;
        // Clamp to valid Kp range and drop obvious fill / corrupt values
        const kp = (isNaN(raw) || raw < 0 || raw > 9) ? 0 : raw;
        return { t: fmtTime(d.time_tag), kp };
      });
  }, [data]);

  return (
    <Card sx={glassCard}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1.5, letterSpacing: 1 }}>
          KP INDEX — LAST 24 HOURS
          <InfoTip text="Storm thresholds: G1=Kp4+, G2=Kp5+, G3=Kp6+, G4=Kp7+, G5=Kp8+" />
        </Typography>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -25 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="t" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis domain={[0, 9]} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} />
            <RTooltip
              {...chartTooltipStyle}
              formatter={(v) => [v.toFixed(1), 'Kp']}
            />
            <ReferenceLine y={4} stroke={C.yellow} strokeDasharray="4 2"
              label={{ value: 'G1', position: 'right', fill: C.yellow, fontSize: 9 }} />
            <ReferenceLine y={5} stroke={C.orange} strokeDasharray="4 2"
              label={{ value: 'G2', position: 'right', fill: C.orange, fontSize: 9 }} />
            <ReferenceLine y={7} stroke={C.red} strokeDasharray="4 2"
              label={{ value: 'G3', position: 'right', fill: C.red, fontSize: 9 }} />
            <Bar dataKey="kp" radius={[3, 3, 0, 0]} maxBarSize={20} minPointSize={2}>
              {chartData.map((d, i) => <Cell key={i} fill={getKpColor(d.kp)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}

function SolarWindChartCard({ data }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    // fill values were replaced with null in the fetcher; keep all rows for
    // a continuous time axis — recharts renders null as a gap in the line.
    return data.slice(-180).map(d => ({
      t:       fmtTime(d.time_tag),
      speed:   d.speed   ?? null,
      density: d.density ?? null,
    }));
  }, [data]);

  // Count how many rows have real speed data
  const validCount = chartData.filter(d => d.speed != null).length;

  return (
    <Card sx={glassCard}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1.5, letterSpacing: 1 }}>
          SOLAR WIND SPEED — LAST 3 HOURS
        </Typography>

        {validCount === 0 ? (
          /* ── No-data empty state ── */
          <Box sx={{
            height: 200, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 1,
            border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 2,
          }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>
              📡 No solar wind data
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.15)', textAlign: 'center', maxWidth: 200 }}>
              DSCOVR/ACE feed temporarily unavailable.
              Data will appear automatically when the stream resumes.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -25 }}>
              <defs>
                <linearGradient id="swGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.cyan} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={C.cyan} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="t" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} domain={['auto', 'auto']} />
              <RTooltip {...chartTooltipStyle}
                formatter={(v, name) =>
                  name === 'speed' ? [`${Math.round(v)} km/s`, 'Speed'] : [`${v?.toFixed(1)} /cm³`, 'Density']
                }
              />
              <ReferenceLine y={400} stroke={C.yellow} strokeDasharray="4 2"
                label={{ value: '400', position: 'insideTopRight', fill: C.yellow, fontSize: 9 }} />
              <ReferenceLine y={600} stroke={C.red} strokeDasharray="4 2"
                label={{ value: '600', position: 'insideTopRight', fill: C.red, fontSize: 9 }} />
              <Area type="monotone" dataKey="speed" name="speed"
                stroke={C.cyan} fill="url(#swGrad)" strokeWidth={2} dot={false} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
}

function BzChartCard({ data }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    // bz_gsm is already null for fill values after normalization in fetcher
    return data.slice(-180).map(d => ({
      t:  fmtTime(d.time_tag),
      bz: d.bz_gsm ?? null,
    }));
  }, [data]);

  return (
    <Card sx={glassCard}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1.5, letterSpacing: 1 }}>
          IMF Bz COMPONENT — LAST 3 HOURS
          <InfoTip text="Negative Bz (southward) opens Earth's magnetosphere to solar wind. Sustained Bz below -10 nT causes geomagnetic storms." />
        </Typography>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -25 }}>
            <defs>
              <linearGradient id="bzGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.green} stopOpacity={0.35} />
                <stop offset="95%" stopColor={C.green} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="t" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} />
            <RTooltip {...chartTooltipStyle}
              formatter={(v) => [`${v?.toFixed(2)} nT`, 'Bz']}
            />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} strokeDasharray="5 3"
              label={{ value: '0 nT', position: 'insideTopRight', fill: 'rgba(255,255,255,0.25)', fontSize: 9 }} />
            <Area type="monotone" dataKey="bz" stroke={C.green} fill="url(#bzGrad)" strokeWidth={2} dot={false} connectNulls name="Bz" />
          </AreaChart>
        </ResponsiveContainer>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 3, bgcolor: C.green, borderRadius: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>Southward — aurora favorable</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 3, bgcolor: C.grey, borderRadius: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>Northward — quiet</Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

function XRayChartCard({ data }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const step = Math.max(1, Math.floor(data.length / 96));
    return data
      .filter((_, i) => i % step === 0)
      .map(d => {
        const flux = d.flux || d.observed_flux || 0;
        return flux > 0 ? { t: fmtTime(d.time_tag), flux } : null;
      })
      .filter(Boolean);
  }, [data]);

  return (
    <Card sx={glassCard}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1, letterSpacing: 1 }}>
          X-RAY FLUX — LAST 24 HOURS
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
          {[['A', C.green], ['B', C.green], ['C', C.yellow], ['M', C.orange], ['X', C.red]].map(([c, col]) => (
            <Chip key={c} label={c} size="small"
              sx={{ bgcolor: `${col}18`, color: col, fontSize: 10, height: 18, minWidth: 22 }} />
          ))}
        </Box>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 5 }}>
            <defs>
              <linearGradient id="xrGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.orange} stopOpacity={0.4} />
                <stop offset="95%" stopColor={C.orange} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="t" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis
              scale="log" domain={['auto', 'auto']} allowDataOverflow
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }}
              tickFormatter={v => { const f = getFlareInfo(v); return `${f.cls}`; }}
            />
            <RTooltip {...chartTooltipStyle}
              formatter={(v) => [fmtFlux(v), 'X-Ray Flux']}
            />
            <Area type="monotone" dataKey="flux" stroke={C.orange} fill="url(#xrGrad)"
              strokeWidth={2} dot={false} connectNulls name="Flux" />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}

function ProtonFluxChartCard({ data }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const step = Math.max(1, Math.floor(data.length / 96));
    return data
      .filter((_, i) => i % step === 0)
      .map(d => {
        const flux = d['>=10 MeV'] || d['>=10MeV'] || d.flux || null;
        return flux > 0 ? { t: fmtTime(d.time_tag), flux } : null;
      })
      .filter(Boolean);
  }, [data]);

  return (
    <Card sx={glassCard}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1.5, letterSpacing: 1 }}>
          PROTON FLUX ≥10 MeV — LAST 24 HOURS
          <InfoTip text="Solar proton events (≥10 pfu) can damage satellite electronics, affect polar aviation communications, and cause radiation hazards." />
        </Typography>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 5 }}>
            <defs>
              <linearGradient id="prGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.purple} stopOpacity={0.4} />
                <stop offset="95%" stopColor={C.purple} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="t" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis scale="log" domain={['auto', 'auto']} allowDataOverflow
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }}
              tickFormatter={v => v >= 1000 ? `${v / 1000}k` : v} />
            <RTooltip {...chartTooltipStyle} formatter={(v) => [`${v.toFixed(2)} pfu`, 'Proton Flux']} />
            <ReferenceLine y={10} stroke={C.red} strokeDasharray="4 2"
              label={{ value: 'Alert 10 pfu', position: 'insideTopLeft', fill: C.red, fontSize: 9 }} />
            <Area type="monotone" dataKey="flux" stroke={C.purple} fill="url(#prGrad)"
              strokeWidth={2} dot={false} connectNulls name="Proton Flux" />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}

function ElectronFluxChartCard({ data }) {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const step = Math.max(1, Math.floor(data.length / 96));
    return data
      .filter((_, i) => i % step === 0)
      .map(d => {
        const flux = d['>2 MeV'] || d['>=2 MeV'] || d['2.0-4.0 MeV'] || d.flux || null;
        return flux > 0 ? { t: fmtTime(d.time_tag), flux } : null;
      })
      .filter(Boolean);
  }, [data]);

  return (
    <Card sx={glassCard}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1.5, letterSpacing: 1 }}>
          ELECTRON FLUX &gt;2 MeV — LAST 24 HOURS
          <InfoTip text="High-energy electrons can charge spacecraft surfaces and damage electronics in high-orbit satellites (GPS, weather sats)." />
        </Typography>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id="elGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.teal} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={C.teal} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="t" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis scale="log" domain={['auto', 'auto']} allowDataOverflow
                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 9 }} />
              <RTooltip {...chartTooltipStyle} formatter={(v) => [`${v.toFixed(2)} e/cm²/s/sr`, 'Electron Flux']} />
              <Area type="monotone" dataKey="flux" stroke={C.teal} fill="url(#elGrad)"
                strokeWidth={2} dot={false} connectNulls name="Electron Flux" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Electron flux data unavailable</Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 4 — AURORA FORECAST
   ═══════════════════════════════════════════════════════════════════════════ */
function AuroraSection({ kpCurrent, forecast }) {
  const [hemi, setHemi] = useState('north');
  const [imgTs, setImgTs] = useState(Date.now());

  const auroraUrl = hemi === 'north'
    ? `https://services.swpc.noaa.gov/images/aurora-forecast-northern-hemisphere.jpg?ts=${imgTs}`
    : `https://services.swpc.noaa.gov/images/aurora-forecast-southern-hemisphere.jpg?ts=${imgTs}`;

  const latTable = [
    { lat: '66°+', kpMin: 1 }, { lat: '62°+', kpMin: 2 }, { lat: '58°+', kpMin: 3 },
    { lat: '54°+', kpMin: 4 }, { lat: '50°+', kpMin: 5 }, { lat: '45°+', kpMin: 6 },
    { lat: '40°+', kpMin: 7 }, { lat: 'Equatorial (18°N)', kpMin: 9.5 },
  ];

  const forecastRows = useMemo(() => {
    if (!forecast?.length) return [];
    return forecast
      .filter(r => Array.isArray(r) && r.length >= 2 && !String(r[0]).startsWith(':'))
      .slice(0, 27)
      .map(r => ({ date: r[0], kp: parseFloat(r[1]) || 0 }))
      .filter(r => r.date && r.date.match(/^\d{4}/));
  }, [forecast]);

  const threeDayCards = forecastRows.slice(0, 3);

  return (
    <Box>
      <SectionHeader title="AURORA FORECAST" subtitle="Visibility probability, 27-day outlook & live oval" emoji="🌌" color={C.green} />

      <Grid container spacing={2}>
        {/* Aurora Oval Image */}
        <Grid item xs={12} md={5}>
          <Card sx={glassCard}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ color: C.green, fontFamily: 'Orbitron', fontSize: 11, fontWeight: 800 }}>
                  AURORA OVAL — LIVE
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <ToggleButtonGroup value={hemi} exclusive size="small"
                    onChange={(_, v) => v && setHemi(v)}>
                    {[['north', 'N. Pole'], ['south', 'S. Pole']].map(([v, l]) => (
                      <ToggleButton key={v} value={v} sx={{
                        fontSize: 10, py: 0.3, px: 1, color: 'text.secondary', border: `1px solid rgba(255,255,255,0.08)`,
                        '&.Mui-selected': { color: C.green, bgcolor: `${C.green}18`, borderColor: `${C.green}40` },
                      }}>{l}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <IconButton size="small" onClick={() => setImgTs(Date.now())} sx={{ color: C.cyan, p: 0.5 }}>
                    <Refresh sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#000', border: `1px solid rgba(105,240,174,0.15)` }}>
                <img src={auroraUrl} alt={`Aurora forecast ${hemi}ern hemisphere`}
                  style={{ width: '100%', display: 'block' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mt: 0.75 }}>
                NOAA/SWPC — auto-updates every 30 min
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Visibility Table */}
        <Grid item xs={12} md={7}>
          <Card sx={{ ...glassCard, height: '100%' }}>
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: C.green, fontFamily: 'Orbitron', fontSize: 11, fontWeight: 800, mb: 1.5 }}>
                AURORA VISIBILITY BY LATITUDE
                <InfoTip text="Aurora visibility depends on Kp index and your geographic latitude. Higher Kp = aurora visible at lower latitudes." />
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Latitude', 'Min. Kp', 'Current Status'].map(h => (
                        <TableCell key={h} sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.06)', py: 0.8 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {latTable.map(row => {
                      const vis = kpCurrent >= row.kpMin;
                      return (
                        <TableRow key={row.lat} sx={{ bgcolor: vis ? 'rgba(105,240,174,0.05)' : 'transparent' }}>
                          <TableCell sx={{ color: 'text.primary', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.8 }}>{row.lat}</TableCell>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.8 }}>
                            <Typography sx={{ color: getKpColor(row.kpMin), fontFamily: 'Orbitron', fontSize: 12, fontWeight: 700 }}>
                              {row.kpMin >= 9 ? '9+' : row.kpMin}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.8 }}>
                            <Chip
                              label={vis ? '✓ VISIBLE' : '✗ NOT VISIBLE'}
                              size="small"
                              sx={{
                                bgcolor: vis ? `${C.green}20` : 'rgba(255,255,255,0.04)',
                                color: vis ? C.green : 'text.secondary',
                                fontSize: 10, fontWeight: vis ? 700 : 400,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <Typography variant="caption" sx={{ color: C.cyan, fontWeight: 700, display: 'block', mb: 0.3 }}>
                  📍 Your Location: Pune, India (18.5°N)
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  Aurora currently&nbsp;
                  <strong style={{ color: C.red }}>NOT visible</strong>
                  &nbsp;at your latitude.
                  Minimum Kp required:&nbsp;
                  <strong style={{ color: C.red }}>9+ (Extreme Storm)</strong>
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* 3-Day Forecast Cards */}
      {threeDayCards.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {threeDayCards.map((day, i) => {
            const color = getKpColor(day.kp);
            const storm = getKpStorm(day.kp);
            return (
              <Grid item xs={12} sm={4} key={i}>
                <Card sx={{ ...glassCard, border: `1px solid ${color}33` }}>
                  <Box sx={{ p: 2.5, textAlign: 'center', background: `linear-gradient(135deg, ${color}08, transparent)` }}>
                    <Typography sx={{ color: 'text.secondary', fontFamily: 'Orbitron', fontSize: 11, mb: 0.3 }}>
                      {['TODAY', 'TOMORROW', 'DAY AFTER'][i]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{day.date}</Typography>
                    <Typography sx={{ color, fontFamily: 'Orbitron', fontSize: 36, fontWeight: 900, my: 0.5, lineHeight: 1 }}>
                      Kp {day.kp.toFixed(0)}
                    </Typography>
                    <Chip label={storm.level} size="small"
                      sx={{ bgcolor: `${color}20`, color, fontFamily: 'Orbitron', fontWeight: 700 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                      {storm.label}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* 27-Day Forecast Table */}
      {forecastRows.length > 0 && (
        <Card sx={{ ...glassCard, mt: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography sx={{ color: C.cyan, fontFamily: 'Orbitron', fontSize: 12, fontWeight: 800, mb: 1.5 }}>
              27-DAY KP FORECAST
            </Typography>
            <TableContainer sx={{
              maxHeight: 320, overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 },
            }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Date', 'Expected Kp', 'Storm Level', 'Outlook'].map(h => (
                      <TableCell key={h} sx={{ bgcolor: '#0d0d0d', color: 'text.secondary', fontSize: 10, fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.1)', py: 0.8 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forecastRows.map((row, i) => {
                    const color = getKpColor(row.kp);
                    const storm = getKpStorm(row.kp);
                    const bgHighlight = row.kp >= 7 ? 'rgba(239,83,80,0.05)'
                      : row.kp >= 5 ? 'rgba(255,167,38,0.04)'
                      : row.kp >= 4 ? 'rgba(255,202,40,0.03)'
                      : 'transparent';
                    return (
                      <TableRow key={i} sx={{ bgcolor: bgHighlight }}>
                        <TableCell sx={{ color: 'text.primary', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.7 }}>{row.date}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.7 }}>
                          <Typography sx={{ color, fontFamily: 'Orbitron', fontSize: 13, fontWeight: 700 }}>{row.kp.toFixed(1)}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.7 }}>
                          <Chip label={storm.level} size="small" sx={{ bgcolor: `${color}18`, color, fontSize: 10 }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.7 }}>
                          <Typography variant="caption" sx={{ color }}>{storm.label}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Card>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 5 — SOLAR ACTIVITY
   ═══════════════════════════════════════════════════════════════════════════ */
function SolarActivitySection({ flares, cmes }) {
  const [wl, setWl] = useState('0171');
  const [sdoTs, setSdoTs] = useState(Date.now());

  const WL_MAP = {
    '0171': { label: '171Å — Corona', url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_512_0171.jpg' },
    '0304': { label: '304Å — Chromosphere', url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_512_0304.jpg' },
    '1600': { label: '1600Å — Upper Photosphere', url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_512_1600.jpg' },
    'HMI':  { label: 'HMI — Magnetic Map', url: 'https://sdo.gsfc.nasa.gov/assets/img/latest/latest_512_HMII.jpg' },
  };

  return (
    <Box>
      <SectionHeader title="SOLAR ACTIVITY" subtitle="Live SDO imagery, flares & CME tracker" emoji="☀️" color={C.orange} />

      <Grid container spacing={2}>
        {/* SDO Sun */}
        <Grid item xs={12} md={5}>
          <Card sx={glassCard}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ color: C.orange, fontFamily: 'Orbitron', fontSize: 11, fontWeight: 800 }}>
                  LIVE SUN — NASA SDO/AIA
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <ToggleButtonGroup value={wl} exclusive size="small" onChange={(_, v) => v && setWl(v)}>
                    {Object.entries(WL_MAP).map(([k, { label }]) => (
                      <ToggleButton key={k} value={k} sx={{
                        fontSize: 9, py: 0.3, px: 0.7, color: 'text.secondary',
                        border: '1px solid rgba(255,255,255,0.08)',
                        '&.Mui-selected': { color: C.orange, bgcolor: `${C.orange}18`, borderColor: `${C.orange}40` },
                      }}>
                        {k === 'HMI' ? 'HMI' : label.split(' — ')[0]}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <IconButton size="small" onClick={() => setSdoTs(Date.now())} sx={{ color: C.cyan, p: 0.5 }}>
                    <Refresh sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: '#000', border: `1px solid rgba(255,167,38,0.18)` }}>
                <img
                  src={`${WL_MAP[wl].url}?ts=${sdoTs}`}
                  alt={`SDO Sun ${WL_MAP[wl].label}`}
                  style={{ width: '100%', display: 'block' }}
                  onError={(e) => { e.target.src = WL_MAP['0171'].url; }}
                />
                <Box sx={{ position: 'absolute', bottom: 6, right: 6, bgcolor: 'rgba(0,0,0,0.75)', borderRadius: 1, px: 1, py: 0.3 }}>
                  <Typography sx={{ color: C.orange, fontSize: 10, fontFamily: 'Orbitron' }}>{WL_MAP[wl].label}</Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mt: 0.75 }}>
                NASA Solar Dynamics Observatory — auto-updates every 15 min
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Flare History */}
        <Grid item xs={12} md={7}>
          <Card sx={{ ...glassCard, height: '100%' }}>
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: C.orange, fontFamily: 'Orbitron', fontSize: 11, fontWeight: 800, mb: 1.5 }}>
                RECENT SOLAR FLARES — LAST 7 DAYS
              </Typography>
              {flares?.length > 0 ? (
                <TableContainer sx={{ maxHeight: 300, overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,167,38,0.2)', borderRadius: 2 } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Time', 'Class', 'Region', 'Duration'].map(h => (
                          <TableCell key={h} sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.06)', py: 0.7 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flares.slice(0, 20).map((f, i) => {
                        const cls = getFlareInfoFromStr(f.classType || f.flareClass);
                        const dur = (f.beginTime && f.endTime)
                          ? `~${Math.round((new Date(f.endTime) - new Date(f.beginTime)) / 60000)}m`
                          : '—';
                        return (
                          <TableRow key={i} hover sx={{ '&:hover': { bgcolor: 'rgba(255,167,38,0.04)' } }}>
                            <TableCell sx={{ color: 'text.secondary', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.75 }}>
                              {fmtDateTime(f.beginTime || f.peakTime)}
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.75 }}>
                              <Chip label={f.classType || f.flareClass || '?'} size="small"
                                sx={{ bgcolor: `${cls.color}22`, color: cls.color, fontSize: 11, fontWeight: 800, fontFamily: 'Orbitron' }} />
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.75 }}>
                              {f.activeRegionNum ? `AR${f.activeRegionNum}` : f.sourceLocation || '—'}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.03)', py: 0.75 }}>
                              {dur}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: C.green }}>✓ No significant flares</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Solar activity is low</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* CME Tracker */}
        {cmes?.length > 0 && (
          <Grid item xs={12}>
            <Card sx={glassCard}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ color: C.red, fontFamily: 'Orbitron', fontSize: 11, fontWeight: 800, mb: 1.5 }}>
                  CME TRACKER — CORONAL MASS EJECTIONS
                  <InfoTip text="CMEs are billion-ton plasma clouds erupted from the Sun. Earth-directed CMEs cause geomagnetic storms 1–4 days after ejection." />
                </Typography>
                <Grid container spacing={1.5}>
                  {cmes.slice(0, 6).map((cme, i) => {
                    const isEarth = cme.note?.toLowerCase().includes('earth') || cme.isMostAccurate;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <Box sx={{
                          p: 1.5, borderRadius: 2,
                          bgcolor: isEarth ? 'rgba(239,83,80,0.07)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${isEarth ? 'rgba(239,83,80,0.3)' : 'rgba(255,255,255,0.05)'}`,
                        }}>
                          {isEarth && (
                            <Chip label="⚠ EARTH-DIRECTED" size="small"
                              sx={{ bgcolor: `${C.red}22`, color: C.red, fontSize: 10, mb: 0.75 }} />
                          )}
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {fmtDateTime(cme.startTime || cme.activityID)}
                          </Typography>
                          <Typography sx={{ color: C.orange, fontSize: 13, fontWeight: 600, mt: 0.3, lineHeight: 1.4 }}>
                            {cme.note?.slice(0, 80) || 'CME Event Detected'}
                          </Typography>
                          {cme.speed && (
                            <Typography variant="caption" sx={{ color: C.cyan, display: 'block', mt: 0.3 }}>
                              Speed: {cme.speed} km/s
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 6 — GEOMAGNETIC STORM TRACKER
   ═══════════════════════════════════════════════════════════════════════════ */
const STORM_DESCRIPTIONS = {
  G0: 'Geomagnetically quiet. No significant effects expected on power grids, satellites, or radio systems.',
  G1: 'Minor storm. Weak power grid fluctuations. Minor impact on satellite operations. Migratory animals may be affected. Aurora visible from 60°+ latitude.',
  G2: 'Moderate storm. High-latitude power systems may experience voltage alarms. HF radio propagation degraded. Aurora visible from 55°+.',
  G3: 'Strong storm. Voltage corrections required on power systems. False alarms on protection devices possible. Satellite drag increases. Aurora visible from 50°+.',
  G4: 'Severe storm. Widespread voltage control problems. HF radio propagation disrupted. Pipeline currents at hazardous levels. Aurora visible from 45°+.',
  G5: 'Extreme storm. Complete collapse of radio communications for days. Widespread power outages possible. Transformer damage risk. Aurora visible near equator.',
};

function StormTrackerSection({ kpCurrent, kpHistory }) {
  const storm = getKpStorm(kpCurrent || 0);
  const isStorming = (kpCurrent || 0) >= 5;
  const kpColor = getKpColor(kpCurrent || 0);

  // Estimate storm duration
  let stormMins = 0;
  if (isStorming && kpHistory?.length) {
    for (let i = kpHistory.length - 1; i >= 0; i--) {
      if (parseFloat(kpHistory[i].kp_index) >= 5) stormMins++;
      else break;
    }
  }

  const scaleItems = [
    { level: 'G1', kpRange: 'Kp 5-6', color: C.yellow },
    { level: 'G2', kpRange: 'Kp 6-7', color: C.orange },
    { level: 'G3', kpRange: 'Kp 7-8', color: '#ff7043' },
    { level: 'G4', kpRange: 'Kp 8-9', color: C.red },
    { level: 'G5', kpRange: 'Kp 9',   color: '#b71c1c' },
  ];

  return (
    <Box>
      <SectionHeader title="GEOMAGNETIC STORM TRACKER" subtitle="Current conditions & NOAA G-scale" emoji="⚡" color={C.orange} />

      {/* Current Status */}
      <Card sx={{ ...glassCard, border: `1px solid ${kpColor}40`, mb: 2 }}>
        <Box sx={{ p: 2.5, background: `linear-gradient(135deg, ${kpColor}06 0%, transparent 70%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography sx={{ color: kpColor, fontFamily: 'Orbitron', fontSize: 44, fontWeight: 900, lineHeight: 1 }}>
                {storm.level}
              </Typography>
              <Typography variant="caption" sx={{ color: kpColor, opacity: 0.7 }}>NOAA Scale</Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: `${kpColor}25` }} />

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ color: kpColor, fontFamily: 'Orbitron', fontSize: 16, fontWeight: 700, mb: 0.5 }}>
                {storm.label}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13, lineHeight: 1.6 }}>
                {STORM_DESCRIPTIONS[storm.level]}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', minWidth: 90 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.3 }}>Current Kp</Typography>
              <Typography sx={{ color: kpColor, fontFamily: 'Orbitron', fontSize: 34, fontWeight: 900, lineHeight: 1 }}>
                {(kpCurrent || 0).toFixed(1)}
              </Typography>
              {isStorming && stormMins > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Active {stormMins}+ min
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Card>

      {/* G-Scale Grid */}
      <Grid container spacing={1.5}>
        {scaleItems.map(({ level, kpRange, color }) => {
          const active = storm.level === level;
          return (
            <Grid item xs={6} sm={4} md={12 / 5} key={level}>
              <Box sx={{
                p: 2, borderRadius: 2,
                border: `1px solid ${active ? color : 'rgba(255,255,255,0.06)'}`,
                bgcolor: active ? `${color}12` : 'rgba(255,255,255,0.02)',
                transition: 'all 0.3s',
                boxShadow: active ? `0 0 20px ${color}25` : 'none',
              }}>
                <Typography sx={{ color: active ? color : 'rgba(255,255,255,0.3)', fontFamily: 'Orbitron', fontSize: 24, fontWeight: 900 }}>
                  {level}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{kpRange}</Typography>
                {STORM_DESCRIPTIONS[level].split('. ').slice(0, 2).map((s, i) => (
                  <Typography key={i} variant="caption" sx={{ color: active ? color : 'rgba(255,255,255,0.3)', display: 'block', fontSize: 10, lineHeight: 1.4 }}>
                    • {s}
                  </Typography>
                ))}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 7 — ALERTS PANEL
   ═══════════════════════════════════════════════════════════════════════════ */
function AlertsPanel({ alerts, isLoading }) {
  const [readSet, setReadSet] = useState(new Set());
  const [filter, setFilter] = useState('ALL');

  const categorize = (msg) => {
    const m = String(msg).toUpperCase();
    if (m.includes('WATCH')) return 'WATCH';
    if (m.includes('WARNING')) return 'WARNING';
    if (m.includes('ALERT')) return 'ALERT';
    if (m.includes('SUMMARY')) return 'SUMMARY';
    return 'INFO';
  };

  const catColor = { WATCH: C.yellow, WARNING: C.orange, ALERT: C.red, SUMMARY: C.cyan, INFO: 'text.secondary' };

  const allFilters = ['ALL', 'ALERT', 'WARNING', 'WATCH', 'SUMMARY'];
  const filtered = useMemo(() =>
    (alerts || []).filter(a => filter === 'ALL' || categorize(a.message) === filter),
    [alerts, filter]
  );

  return (
    <Box>
      <SectionHeader title="NOAA SPACE WEATHER ALERTS" subtitle="Live alert feed — auto-refreshes every 5 min" emoji="🚨" color={C.red} />
      <Card sx={glassCard}>
        <Box sx={{ p: 2 }}>
          {/* Filter chips */}
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {allFilters.map(f => (
              <Chip key={f} label={f} size="small" onClick={() => setFilter(f)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: filter === f ? `${C.cyan}20` : 'rgba(255,255,255,0.04)',
                  color: filter === f ? C.cyan : 'text.secondary',
                  border: filter === f ? `1px solid ${C.cyan}40` : '1px solid transparent',
                  '&:hover': { bgcolor: `${C.cyan}12` },
                  fontSize: 11,
                }}
              />
            ))}
            <Chip label={`${filtered.length} alerts`} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.04)', color: 'text.secondary', ml: 'auto', fontSize: 11 }} />
          </Box>

          {/* Alert list */}
          <Box sx={{
            maxHeight: 420, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 1,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(239,83,80,0.2)', borderRadius: 2 },
          }}>
            {isLoading ? (
              Array(3).fill(0).map((_, i) => (
                <Skeleton key={i} height={80} sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2 }} />
              ))
            ) : filtered.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography sx={{ color: C.green, fontSize: 18 }}>✓</Typography>
                <Typography sx={{ color: C.green, fontWeight: 600 }}>No Active Alerts</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Space weather conditions are currently quiet</Typography>
              </Box>
            ) : (
              filtered.slice(0, 25).map((alert, i) => {
                const cat = categorize(alert.message);
                const color = catColor[cat] || C.cyan;
                const isRead = readSet.has(i);
                return (
                  <Box key={i} sx={{
                    p: 1.5, borderRadius: 2,
                    border: `1px solid ${isRead ? 'rgba(255,255,255,0.04)' : color + '30'}`,
                    bgcolor: isRead ? 'rgba(255,255,255,0.01)' : `${color}07`,
                    opacity: isRead ? 0.55 : 1,
                    transition: 'all 0.2s',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={cat} size="small"
                          sx={{ bgcolor: `${color}22`, color, fontSize: 10, fontWeight: 700, height: 20 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                          {alert.issue_datetime?.slice(0, 16)}
                        </Typography>
                      </Box>
                      {!isRead && (
                        <Button size="small" onClick={() => setReadSet(s => new Set([...s, i]))}
                          sx={{ color: 'text.secondary', fontSize: 10, minWidth: 0, p: '2px 6px' }}>
                          Mark read
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{
                      color: isRead ? 'rgba(255,255,255,0.35)' : 'text.primary',
                      fontSize: 12, lineHeight: 1.55,
                      display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {alert.message}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 8 — HISTORICAL ARCHIVE
   ═══════════════════════════════════════════════════════════════════════════ */
const HISTORICAL_EVENTS = [
  { date: 'Oct 28-29, 2003', name: 'Halloween Storms', kp: 9, color: C.red, desc: 'X17 and X28 class flares — among the strongest ever recorded. Satellite failures, power outages in Sweden, GPS disrupted globally.' },
  { date: 'Sep 1-2, 1859', name: 'Carrington Event', kp: 9, color: C.red, desc: 'Strongest geomagnetic storm in recorded history. Worldwide telegraph failures; operators reported electric shocks and papers catching fire.' },
  { date: 'Mar 13, 1989', name: 'Quebec Blackout', kp: 9, color: C.red, desc: 'X15 flare caused a 9-hour blackout affecting 6 million Canadians. Transformers damaged, satellites experienced anomalies.' },
  { date: 'Jul 14, 2000', name: 'Bastille Day Event', kp: 9, color: C.orange, desc: 'X5.7 flare and full halo CME. Proton event reached record levels. Multiple spacecraft damaged.' },
  { date: 'Nov 4, 2003', name: 'X28+ Megaflare', kp: 8, color: C.red, desc: 'Largest X-ray flare ever recorded — off the GOES scale. Caused immediate HF radio blackout on Earth\'s sunlit face.' },
];

function ArchiveSection() {
  return (
    <Box>
      <SectionHeader title="SPACE WEATHER HISTORY" subtitle="Notable historical events & context" emoji="📚" />
      <Grid container spacing={2}>
        {HISTORICAL_EVENTS.map((evt, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card sx={{ ...glassCard, border: `1px solid ${evt.color}20`, height: '100%', transition: 'all 0.2s',
              '&:hover': { border: `1px solid ${evt.color}50`, transform: 'translateY(-2px)' } }}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{evt.date}</Typography>
                  <Chip label={`Kp ${evt.kp}`} size="small"
                    sx={{ bgcolor: `${evt.color}20`, color: evt.color, fontFamily: 'Orbitron', fontSize: 10 }} />
                </Box>
                <Typography sx={{ color: evt.color, fontWeight: 800, fontSize: 15, mb: 0.75 }}>{evt.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{evt.desc}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 10 — QUICK LINKS
   ═══════════════════════════════════════════════════════════════════════════ */
function QuickLinksRow() {
  const navigate = useNavigate();
  const links = [
    { emoji: '🔭', label: 'Telescope Feed', path: '/telescope',          color: C.purple },
    { emoji: '🛸', label: 'Satellite Tracker', path: '/satellite-tracker', color: C.cyan   },
    { emoji: '🌌', label: 'Night Sky',          path: '/night-sky',         color: C.teal   },
    { emoji: '🌙', label: 'Moon Dashboard',     path: '/moon',              color: '#cfd8dc' },
    { emoji: '🤖', label: 'Ask NOVA AI',         path: '/ai-chat',           color: C.green  },
    { emoji: '📰', label: 'Space News',          path: '/space-news',        color: C.orange },
  ];
  return (
    <Box>
      <SectionHeader title="QUICK ACCESS" subtitle="Navigate to other SpacePulse features" emoji="🚀" />
      <Grid container spacing={1.5}>
        {links.map(link => (
          <Grid item xs={6} sm={4} md={2} key={link.path}>
            <Card
              onClick={() => navigate(link.path)}
              sx={{
                ...glassCard, cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  border: `1px solid ${link.color}50`,
                  boxShadow: `0 8px 32px ${link.color}20`,
                },
              }}
            >
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 30, mb: 0.5 }}>{link.emoji}</Typography>
                <Typography sx={{ color: link.color, fontSize: 11, fontWeight: 600, fontFamily: 'Orbitron', letterSpacing: 0.5 }}>
                  {link.label}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ERROR BOUNDARY
   ═══════════════════════════════════════════════════════════════════════════ */
class SectionBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return (
      <Alert severity="error" sx={{ borderRadius: 2, mb: 1 }}
        action={<Button size="small" onClick={() => this.setState({ error: null })}>Retry</Button>}>
        {this.props.title ? `${this.props.title} failed to render: ` : ''}{this.state.error.message}
      </Alert>
    );
    return this.props.children;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   LOADING SKELETON
   ═══════════════════════════════════════════════════════════════════════════ */
function GaugeSkeleton() {
  return (
    <Card sx={{ ...glassCard, height: '100%' }}>
      <Box sx={{ p: 2 }}>
        <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 1.5 }} width="55%" height={14} />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Skeleton variant="circular" width={140} height={140} sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
        </Box>
        <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
        <Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} width="70%" />
      </Box>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION — IMPACT ANALYZER
   ═══════════════════════════════════════════════════════════════════════════ */
const IMPACT_LEVEL = {
  Low:      { color: '#69f0ae', bg: 'rgba(105,240,174,0.10)', icon: CheckCircle,  label: 'All systems normal'              },
  Moderate: { color: '#ffca28', bg: 'rgba(255,202,40,0.10)',  icon: WarningAmber, label: 'Minor disruptions possible'      },
  High:     { color: '#ef5350', bg: 'rgba(239,83,80,0.10)',   icon: ErrorOutline, label: 'Significant disruptions likely'  },
};

function ImpactCard({ icon: Icon, label, value, levelColor }) {
  return (
    <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Icon sx={{ color: levelColor, fontSize: 20 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, lineHeight: 1.5 }}>{value}</Typography>
      </Box>
    </Card>
  );
}

function ImpactKpTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const kp = payload[0].value;
  return (
    <Box sx={{ bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: getKpColor(kp), fontWeight: 700 }}>Kp {kp}</Typography>
    </Box>
  );
}

const NOAA_KP_URL = 'https://services.swpc.noaa.gov/products/noaa-planetary-k-index.json';

function buildImpactFromKp(kp, trend) {
  let level, gps, internet, flight, why;
  if (kp >= 7) {
    level   = 'High';
    gps     = 'Severe accuracy loss (±50 m+)';
    internet = 'Satellite communication unstable';
    flight  = 'Polar route disruption likely';
    why     = `Space weather is SEVERE. The planetary Kp index is ${kp.toFixed(1)} — a major geomagnetic storm (G${kp>=9?5:kp>=8?4:3}-class). Charged solar particles are intensely disturbing Earth's magnetosphere, causing GPS signal degradation, satellite instability, and disrupting polar aviation routes.`;
  } else if (kp >= 5) {
    level   = 'Moderate';
    gps     = 'Moderate GPS errors (±15–50 m)';
    internet = 'Minor satellite latency';
    flight  = 'Possible polar rerouting';
    why     = `Space weather is MODERATELY active. The Kp index is ${kp.toFixed(1)} (G${kp>=6?2:1}-class storm). Expect minor GPS errors, slight satellite communication delays, and possible polar flight rerouting.`;
  } else {
    level   = 'Low';
    gps     = 'Normal — no impact';
    internet = 'Stable — no impact';
    flight  = 'No impact';
    why     = `Space weather is calm. Kp index is ${kp.toFixed(1)} — well within the quiet range. Earth's magnetosphere is undisturbed. All systems operating normally.`;
  }
  return { level, kpIndex: Math.round(kp * 10) / 10, flare: 'N/A', gpsImpact: gps, internetImpact: internet, flightImpact: flight, whyExplanation: why, kpTrend: trend, timestamp: new Date().toISOString() };
}

function ImpactAnalyzerSection({ kpCurrent, kpHistory }) {
  const data = useMemo(() => {
    if (kpCurrent == null) return null;
    const trend = (kpHistory || [])
      .slice(-24)
      .map(d => ({
        time: d.time_tag ? String(d.time_tag).substring(11, 16) : '',
        kp:   Math.round(safeKp(d) * 10) / 10,
      }))
      .filter(p => p.kp > 0);
    return buildImpactFromKp(kpCurrent, trend);
  }, [kpCurrent, kpHistory]);

  const loading = kpCurrent == null;

  return (
    <Box>
      <SectionHeader title="SPACE WEATHER IMPACT" subtitle="Real-time effect on GPS, internet & aviation" emoji="🛰️" color={C.orange} />

      {data?.level === 'High' && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, bgcolor: 'rgba(239,83,80,0.10)', border: '1px solid rgba(239,83,80,0.35)', color: '#ef5350' }}>
          <strong>High activity detected</strong> — GPS, satellite comms, and polar flight routes may be disrupted.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} sx={{ color: C.orange }} />
        </Box>
      ) : data ? (() => {
        const cfg = IMPACT_LEVEL[data.level] || IMPACT_LEVEL.Low;
        const LevelIcon = cfg.icon;
        const trend = data.kpTrend || [];

        return (
          <Box>
            {/* Level + Kp + Flare row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={5}>
                <Card sx={{ background: `linear-gradient(135deg,${cfg.bg} 0%,rgba(8,8,8,0.95) 100%)`, border: `1px solid ${cfg.color}35`, borderRadius: 2 }}>
                  <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LevelIcon sx={{ fontSize: 40, color: cfg.color }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 900, color: cfg.color, lineHeight: 1 }}>{data.level}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{cfg.label}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3.5}>
                <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>Kp Index</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: getKpColor(data.kpIndex || 0), lineHeight: 1, mb: 1 }}>{data.kpIndex}</Typography>
                    <LinearProgress variant="determinate" value={((data.kpIndex || 0) / 9) * 100}
                      sx={{ height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.07)', '& .MuiLinearProgress-bar': { bgcolor: getKpColor(data.kpIndex || 0) } }} />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3.5}>
                <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>Source</Typography>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.5 }}>NOAA SWPC</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>noaa-planetary-k-index</Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Impact cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}><ImpactCard icon={GpsFixed}  label="GPS"               value={data.gpsImpact}      levelColor={cfg.color} /></Grid>
              <Grid item xs={12} sm={4}><ImpactCard icon={Wifi}      label="Internet/Satellite" value={data.internetImpact} levelColor={cfg.color} /></Grid>
              <Grid item xs={12} sm={4}><ImpactCard icon={Flight}    label="Aviation"           value={data.flightImpact}   levelColor={cfg.color} /></Grid>
            </Grid>

            {/* Kp trend chart */}
            {trend.length > 0 && (
              <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, mb: 2 }}>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Kp Index — Last 24 Readings</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                      Auto-refreshes every 60 s
                    </Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={trend} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                      <defs>
                        <linearGradient id="kpImpactGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={C.orange} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={C.orange} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fill: '#555', fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
                      <YAxis domain={[0, 9]} tick={{ fill: '#555', fontSize: 10 }} tickLine={false} />
                      <RTooltip content={<ImpactKpTooltip />} />
                      <ReferenceLine y={5} stroke={C.yellow} strokeDasharray="4 4" />
                      <ReferenceLine y={7} stroke={C.red}    strokeDasharray="4 4" />
                      <Area type="monotone" dataKey="kp" stroke={C.orange} strokeWidth={2} fill="url(#kpImpactGrad)" dot={false} activeDot={{ r: 4, fill: C.orange }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            )}

            {/* Why explanation */}
            {data.whyExplanation && (
              <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
                <Box sx={{ p: 2.5 }}>
                  <Typography variant="body2" sx={{ color: C.orange, fontWeight: 700, mb: 1 }}>Why is this happening?</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.8 }}>{data.whyExplanation}</Typography>
                  {data.timestamp && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1.5, display: 'block' }}>
                      Updated: {new Date(data.timestamp).toLocaleString()} · Auto-refreshes every 5 min
                    </Typography>
                  )}
                </Box>
              </Card>
            )}
          </Box>
        );
      })() : null}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
export default function SpaceWeatherDashboard() {
  // ── State ──────────────────────────────────────────────────────────────────
  const [kpData,       setKpData]       = useState(null);
  const [kpHistory,    setKpHistory]    = useState([]);
  const [windData,     setWindData]     = useState(null);
  const [windHistory,  setWindHistory]  = useState([]);
  const [bzData,       setBzData]       = useState(null);
  const [bzHistory,    setBzHistory]    = useState([]);
  const [xrayData,     setXrayData]     = useState(null);
  const [xrayHistory,  setXrayHistory]  = useState([]);
  const [protonData,   setProtonData]   = useState([]);
  const [electronData, setElectronData] = useState([]);
  const [alerts,       setAlerts]       = useState([]);
  const [forecast,     setForecast]     = useState([]);
  const [flares,       setFlares]       = useState([]);
  const [cmes,         setCmes]         = useState([]);
  const [lastUpdated,  setLastUpdated]  = useState(null);
  const [isLoading,    setIsLoading]    = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);

  // ── Safe fetch wrapper ─────────────────────────────────────────────────────
  const safeFetch = useCallback(async (url, onSuccess) => {
    try {
      const res = await axios.get(url, { timeout: 12000 });
      if (res.data) onSuccess(res.data);
    } catch (err) {
      console.warn(`[SpaceWeather] Failed to fetch ${url}:`, err.message);
    }
  }, []);

  // ── Live data (Kp, Wind, Bz, X-ray) — every 60 seconds ───────────────────
  const fetchLiveData = useCallback(async () => {
    await Promise.allSettled([

      // Kp Index ─────────────────────────────────────────────────────────────
      safeFetch(
        'https://services.swpc.noaa.gov/json/planetary_k_index_1m.json',
        (data) => {
          if (!Array.isArray(data) || !data.length) return;
          // Kp values are reliably numeric; just take the last entry
          setKpData(data[data.length - 1]);
          setKpHistory(data.slice(-1440)); // last 24h at ~1/min
        }
      ),

      // Solar Wind ────────────────────────────────────────────────────────────
      // NOAA fill value is -9999.9 (|v| > 999). We keep ALL rows for a
      // continuous time axis and replace fill values with null so recharts
      // renders clean gaps. Valid solar wind speed is always 200–3000 km/s.
      safeFetch(
        'https://services.swpc.noaa.gov/json/rtsw/rtsw_wind_1m.json',
        (data) => {
          if (!Array.isArray(data) || !data.length) return;

          // True if the value is the NOAA -9999.9 fill sentinel or absent
          const isFill = (v) => v == null || Math.abs(parseFloat(v)) > 999;

          const normalized = data.map(d => {
            // NOAA may use 'speed' or 'proton_speed' or 'bulk_speed'
            const rawSpeed = d.speed ?? d.proton_speed ?? d.bulk_speed;
            const speed = isFill(rawSpeed) ? null : Math.abs(parseFloat(rawSpeed));
            const density = isFill(d.density) ? null : parseFloat(d.density);
            const temperature = isFill(d.temperature) ? null : parseFloat(d.temperature);
            return { time_tag: d.time_tag, speed, density, temperature };
          });

          // Gauge: last row with a real speed reading
          const lastValid = [...normalized].reverse().find(d => d.speed != null && d.speed > 50);
          setWindData(lastValid ?? null);
          // Chart: keep all 180 rows — nulls render as gaps in recharts
          setWindHistory(normalized.slice(-180));
        }
      ),

      // IMF Bz ────────────────────────────────────────────────────────────────
      safeFetch(
        'https://services.swpc.noaa.gov/json/rtsw/rtsw_mag_1m.json',
        (data) => {
          if (!Array.isArray(data) || !data.length) return;

          const isFill = (v) => v == null || Math.abs(v) > 999;

          const normalized = data.map(d => ({
            time_tag: d.time_tag,
            bz_gsm:   isFill(d.bz_gsm) ? null : parseFloat(d.bz_gsm),
            bt:       isFill(d.bt)      ? null : parseFloat(d.bt),
          }));

          const lastValid = [...normalized].reverse().find(d => d.bz_gsm != null);
          setBzData(lastValid || null);
          setBzHistory(normalized.slice(-180));
        }
      ),

      // X-Ray Flux ────────────────────────────────────────────────────────────
      safeFetch(
        'https://services.swpc.noaa.gov/json/goes/primary/xrays-1-day.json',
        (data) => {
          if (!Array.isArray(data) || !data.length) return;
          // Prefer the 0.1–0.8 nm (1–8 Å) short-wave channel used for flare classification
          const sw = data.filter(d => {
            const e = String(d.energy || '');
            return e.includes('0.1-0.8') || e.includes('1-8') || e === '';
          });
          const src = sw.length ? sw : data;
          // Replace fill/zero flux values with null
          const normalized = src.map(d => ({
            time_tag:      d.time_tag,
            flux:          (d.flux > 0) ? d.flux : (d.observed_flux > 0 ? d.observed_flux : null),
            observed_flux: d.observed_flux > 0 ? d.observed_flux : null,
          }));
          const lastValid = [...normalized].reverse().find(d => d.flux != null);
          setXrayData(lastValid || normalized[normalized.length - 1]);
          setXrayHistory(normalized.slice(-288));
        }
      ),

    ]);
    setLastUpdated(new Date());
    setIsLoading(false);
  }, [safeFetch]);

  // ── Particle flux (every 5 min) ───────────────────────────────────────────
  const fetchParticleData = useCallback(async () => {
    await Promise.allSettled([
      safeFetch(
        'https://services.swpc.noaa.gov/json/goes/primary/integral-protons-1-day.json',
        (data) => Array.isArray(data) && setProtonData(data.slice(-288))
      ),
      safeFetch(
        'https://services.swpc.noaa.gov/json/goes/primary/differential-electrons-6-hour.json',
        (data) => Array.isArray(data) && setElectronData(data.slice(-144))
      ),
    ]);
  }, [safeFetch]);

  // ── NOAA Alerts (every 5 min) ──────────────────────────────────────────────
  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    await safeFetch(
      'https://services.swpc.noaa.gov/products/alerts.json',
      (data) => Array.isArray(data) && setAlerts(data.slice(0, 50))
    );
    setAlertsLoading(false);
  }, [safeFetch]);

  // ── Forecast (every hour) ─────────────────────────────────────────────────
  const fetchForecast = useCallback(async () => {
    await safeFetch(
      'https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json',
      (data) => Array.isArray(data) && setForecast(data)
    );
  }, [safeFetch]);

  // ── DONKI (NASA) flares & CMEs — via backend (real API key) ──────────────
  const fetchDonki = useCallback(async () => {
    const API_BASE = process.env.REACT_APP_API_URL || '/api';
    const end   = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - 7 * 86400_000).toISOString().slice(0, 10);
    await Promise.allSettled([
      safeFetch(
        `${API_BASE}/events?type=SOLAR_FLARE&startDate=${start}&endDate=${end}&size=50`,
        (res) => res?.data?.content && setFlares([...res.data.content].reverse())
      ),
      safeFetch(
        `${API_BASE}/events?type=CME&startDate=${start}&endDate=${end}&size=50`,
        (res) => res?.data?.content && setCmes([...res.data.content].reverse())
      ),
    ]);
  }, [safeFetch]);

  // ── Bootstrap on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchLiveData();
    fetchParticleData();
    fetchAlerts();
    fetchForecast();
    fetchDonki();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-refresh intervals ─────────────────────────────────────────────────
  useInterval(fetchLiveData,     60_000);     // 1 min
  useInterval(fetchParticleData, 300_000);    // 5 min
  useInterval(fetchAlerts,       300_000);    // 5 min
  useInterval(fetchForecast,     3_600_000);  // 1 hour
  useInterval(fetchDonki,        900_000);    // 15 min

  const kpCurrent = parseFloat(kpData?.kp_index) || 0;

  /* ─ Render ─────────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ bgcolor: '#000000', minHeight: '100vh' }}>
      {/* SECTION 1 — Live Status Bar */}
      <SectionBoundary title="Status Bar">
        <LiveStatusBar
          kpData={kpData} windData={windData} bzData={bzData} xrayData={xrayData}
          lastUpdated={lastUpdated} isLoading={isLoading} onRefresh={fetchLiveData}
        />
      </SectionBoundary>

      <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1600, mx: 'auto' }}>
        {/* Page Header */}
        <Box sx={{ mb: 3.5, mt: 1 }}>
          <Typography sx={{
            fontFamily: 'Orbitron, monospace',
            fontSize: { xs: 20, sm: 26, md: 32 },
            fontWeight: 900,
            letterSpacing: 2,
            background: 'linear-gradient(90deg, #ffffff 0%, #69f0ae 60%, #ffffff 100%)',
            backgroundSize: '200% auto',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmer 4s linear infinite',
            '@keyframes shimmer': { '0%': { backgroundPosition: '0% center' }, '100%': { backgroundPosition: '200% center' } },
          }}>
            SPACE WEATHER DASHBOARD
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
            Real-time data from NOAA SWPC & NASA SDO · Gauges refresh every 60 s · All times UTC
          </Typography>
        </Box>

        {/* SECTION 2 — Gauges Row */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader title="REAL-TIME CONDITIONS" subtitle="Live instrument readings" emoji="📡" />
          <Grid container spacing={2}>
            {isLoading
              ? [0, 1, 2, 3].map(i => <Grid item xs={12} sm={6} md={3} key={i}><GaugeSkeleton /></Grid>)
              : [
                  <Grid item xs={12} sm={6} md={3} key="kp">
                    <SectionBoundary title="Kp Gauge">
                      <KpGaugeCard kpData={kpData} kpHistory={kpHistory} />
                    </SectionBoundary>
                  </Grid>,
                  <Grid item xs={12} sm={6} md={3} key="wind">
                    <SectionBoundary title="Wind Gauge">
                      <SolarWindGaugeCard windData={windData} windHistory={windHistory} />
                    </SectionBoundary>
                  </Grid>,
                  <Grid item xs={12} sm={6} md={3} key="bz">
                    <SectionBoundary title="Bz Gauge">
                      <BzGaugeCard bzData={bzData} />
                    </SectionBoundary>
                  </Grid>,
                  <Grid item xs={12} sm={6} md={3} key="xray">
                    <SectionBoundary title="X-Ray Gauge">
                      <XRayGaugeCard xrayData={xrayData} xrayHistory={xrayHistory} />
                    </SectionBoundary>
                  </Grid>,
                ]
            }
          </Grid>
        </Box>

        {/* SECTION 3 — Charts */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader title="REAL-TIME GRAPHS" subtitle="24-hour trend data" emoji="📊" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><SectionBoundary title="Kp Chart"><KpBarChartCard data={kpHistory} /></SectionBoundary></Grid>
            <Grid item xs={12} md={6}><SectionBoundary title="Wind Chart"><SolarWindChartCard data={windHistory} /></SectionBoundary></Grid>
            <Grid item xs={12} md={6}><SectionBoundary title="Bz Chart"><BzChartCard data={bzHistory} /></SectionBoundary></Grid>
            <Grid item xs={12} md={6}><SectionBoundary title="X-Ray Chart"><XRayChartCard data={xrayHistory} /></SectionBoundary></Grid>
            <Grid item xs={12} md={6}><SectionBoundary title="Proton Chart"><ProtonFluxChartCard data={protonData} /></SectionBoundary></Grid>
            <Grid item xs={12} md={6}><SectionBoundary title="Electron Chart"><ElectronFluxChartCard data={electronData} /></SectionBoundary></Grid>
          </Grid>
        </Box>

        {/* SECTION 4 — Aurora */}
        <Box sx={{ mb: 5 }}>
          <SectionBoundary title="Aurora">
            <AuroraSection kpCurrent={kpCurrent} forecast={forecast} />
          </SectionBoundary>
        </Box>

        {/* SECTION 5 — Solar Activity */}
        <Box sx={{ mb: 5 }}>
          <SectionBoundary title="Solar Activity">
            <SolarActivitySection flares={flares} cmes={cmes} />
          </SectionBoundary>
        </Box>

        {/* SECTION 5b — Impact Analyzer */}
        <Box sx={{ mb: 5 }}>
          <SectionBoundary title="Impact Analyzer">
            <ImpactAnalyzerSection kpCurrent={kpCurrent} kpHistory={kpHistory} />
          </SectionBoundary>
        </Box>

        {/* SECTION 6 — Storm Tracker */}
        <Box sx={{ mb: 5 }}>
          <SectionBoundary title="Storm Tracker">
            <StormTrackerSection kpCurrent={kpCurrent} kpHistory={kpHistory} />
          </SectionBoundary>
        </Box>

        {/* SECTION 7 — Alerts */}
        <Box sx={{ mb: 5 }}>
          <SectionBoundary title="Alerts">
            <AlertsPanel alerts={alerts} isLoading={alertsLoading} />
          </SectionBoundary>
        </Box>

        {/* SECTION 8 — Archive */}
        <Box sx={{ mb: 5 }}>
          <SectionBoundary title="Archive">
            <ArchiveSection />
          </SectionBoundary>
        </Box>


        {/* Footer */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />
        <Box sx={{ textAlign: 'center', pb: 3 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 11 }}>
            Data: NOAA/SWPC (swpc.noaa.gov) · NASA SDO (sdo.gsfc.nasa.gov) · NASA DONKI (kauai.ccmc.gsfc.nasa.gov)
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.15)', display: 'block', fontSize: 10, mt: 0.3 }}>
            SpacePulse Space Weather Dashboard © {new Date().getFullYear()} · All data provided for informational purposes
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
